import { AnimatePresence, motion } from "framer-motion";
import { useLiveQuery } from "dexie-react-hooks";
import { FileSpreadsheet, X } from "lucide-react";
import React, { useMemo, useState } from "react";
import { cn } from "@repo/core";
import { useCategories } from "../hooks/useBudgetData";
import { PERSON_CANONICAL, personCanonical } from "../i18n/labels";
import { useT } from "../i18n/useT";
import { db } from "../lib/db";
import { handleFormSubmit, onEnterToSubmitKeyDown } from "../lib/formKeyboard";
import {
  buildPreview,
  parseCsvText,
  previewToTransactions,
  suggestMapping,
  type CsvColumnKey,
  type CsvMapping,
} from "../lib/csvImport";

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CsvImportModal({ open, onClose }: Props) {
  const { t, locale } = useT();
  const categories = useCategories();
  const accounts = useLiveQuery(() => db.accounts.toArray(), []) ?? [];
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<CsvMapping>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const defaultAccountId = accounts[0]?.id ?? "";

  const columnOptions: { value: CsvColumnKey; label: string }[] = useMemo(
    () => [
      { value: "skip", label: t("csv.ignore") },
      { value: "date", label: t("tx.col.date") },
      { value: "amount", label: t("tx.col.amount") },
      { value: "type", label: t("tx.col.type") },
      { value: "category", label: t("tx.col.category") },
      { value: "account", label: t("tx.col.account") },
      { value: "person", label: t("tx.col.person") },
      { value: "notes", label: t("tx.col.notes") },
    ],
    [t, locale]
  );

  const preview = useMemo(
    () =>
      headers.length
        ? buildPreview(
            { headers, rows },
            mapping,
            categories,
            defaultAccountId,
            PERSON_CANONICAL.household,
            {
              badDate: t("csv.badDate"),
              badAmount: t("csv.badAmount"),
              noCategory: t("csv.noCategory"),
            }
          )
        : [],
    [headers, rows, mapping, categories, defaultAccountId, t]
  );

  const validCount = preview.filter((r) => r.valid).length;

  const onFile = async (file: File) => {
    setError(null);
    const text = await file.text();
    const parsed = parseCsvText(text);
    if (!parsed.headers.length) {
      setError(t("csv.readError"));
      return;
    }
    setHeaders(parsed.headers);
    setRows(parsed.rows);
    setMapping(suggestMapping(parsed.headers));
  };

  const handleImport = async () => {
    if (!validCount) return;
    setBusy(true);
    try {
      const txs = previewToTransactions(preview).map((tx) => ({
        ...tx,
        person: personCanonical(tx.person, t),
      }));
      await db.transactions.bulkAdd(txs);
      onClose();
      setHeaders([]);
      setRows([]);
      setMapping({});
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="relative z-10 w-full max-w-2xl max-h-[90vh] flex flex-col bg-card border border-border rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <div className="flex items-center gap-2">
                <FileSpreadsheet size={18} className="text-primary" />
                <h2 className="font-semibold">{t("csv.title")}</h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-lg hover:bg-muted"
                aria-label={t("common.close")}
              >
                <X size={16} />
              </button>
            </div>

            <form
              className="flex flex-col flex-1 min-h-0"
              onKeyDown={onEnterToSubmitKeyDown}
              onSubmit={(e) => handleFormSubmit(e, () => handleImport())}
            >
            <div className="p-5 overflow-y-auto space-y-4 flex-1">
              <p className="text-xs text-muted-foreground">{t("csv.intro")}</p>
              {!defaultAccountId && (
                <p className="text-xs text-amber-700 dark:text-amber-400 rounded-lg border border-amber-300/50 bg-amber-50 dark:bg-amber-950/30 px-3 py-2">
                  {t("csv.needAccount")}
                </p>
              )}
              <input
                type="file"
                accept=".csv,text/csv"
                className="block w-full text-xs"
                disabled={!defaultAccountId}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onFile(f);
                }}
              />
              {error && <p className="text-xs text-destructive">{error}</p>}

              {headers.length > 0 && (
                <>
                  <div>
                    <p className="text-xs font-semibold mb-1">{t("csv.mapping")}</p>
                    <p className="text-[10px] text-muted-foreground mb-2">{t("csv.mappingHint")}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {headers.map((h, i) => (
                        <label key={`${h}-${i}`} className="flex items-center gap-2 text-xs">
                          <span
                            className="w-28 shrink-0 truncate text-muted-foreground font-mono text-[10px]"
                            title={h}
                          >
                            {h}
                          </span>
                          <select
                            className="app-input flex-1 min-h-[40px]"
                            value={mapping[h] ?? "skip"}
                            onChange={(e) =>
                              setMapping((m) => ({ ...m, [h]: e.target.value as CsvColumnKey }))
                            }
                          >
                            {columnOptions.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs font-semibold mb-2">
                      {t("csv.preview", { ok: validCount, total: preview.length })}
                    </p>
                    <div className="border border-border rounded-lg overflow-hidden max-h-56 overflow-y-auto text-[11px]">
                      <table className="w-full">
                        <thead className="bg-muted/50 sticky top-0">
                          <tr>
                            <th className="text-left p-2">{t("tx.col.date")}</th>
                            <th className="text-left p-2">{t("tx.col.amount")}</th>
                            <th className="text-left p-2">{t("tx.col.type")}</th>
                            <th className="text-left p-2">{t("tx.col.category")}</th>
                            <th className="text-left p-2">{t("csv.ok")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {preview.slice(0, 40).map((r, i) => (
                            <tr key={i} className={cn("border-t border-border", !r.valid && "bg-destructive/5")}>
                              <td className="p-2 font-mono">{r.date}</td>
                              <td className="p-2">{r.amount}</td>
                              <td className="p-2">{t(`type.${r.type}`)}</td>
                              <td className="p-2">{r.categoryLabel}</td>
                              <td className="p-2">{r.valid ? t("csv.ok") : r.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 px-5 py-4 border-t border-border">
              <button type="button" className="app-btn-outline min-h-[44px]" onClick={onClose}>
                {t("common.cancel")}
              </button>
              <button
                type="submit"
                className="app-btn min-h-[44px]"
                disabled={!validCount || busy || !defaultAccountId}
              >
                {busy ? t("csv.importing") : t("csv.importN", { n: validCount })}
              </button>
            </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
